import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { FiBriefcase, FiCalendar, FiEye } from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import EmptyState from "../components/EmptyState";
import NoData from "../components/NoData";
import { useAuth } from "../contexts/AuthContext";
import { extractList } from "../utils/api";
import { formatDate, timeAgo, statusLabel, statusTone } from "../utils/format";

const CompaniesPage = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("TODOS");
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined,
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef(null);

  const canAccessCompanies = ["POWER_USER", "ADMIN"].includes(user?.role);

  const fetchCompanies = async () => {
    if (!canAccessCompanies) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get("/companies", {
        params: {
          itemsPerPage: -1,
        },
      });
      setCompanies(extractList(response));
    } catch (error) {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [canAccessCompanies]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dateRangeLabel = useMemo(() => {
    if (!dateRange.from && !dateRange.to) {
      return "Selecionar intervalo";
    }

    if (dateRange.from && !dateRange.to) {
      return `De ${dateRange.from.toLocaleDateString("pt-BR")}`;
    }

    if (dateRange.from && dateRange.to) {
      return `${dateRange.from.toLocaleDateString("pt-BR")} - ${dateRange.to.toLocaleDateString("pt-BR")}`;
    }

    return "Selecionar intervalo";
  }, [dateRange.from, dateRange.to]);

  const filteredCompanies = companies.filter((company) => {
    const companyName =
      company?.nomeFantasia || company?.razaoSocial || company?.name || "";
    const companyEmail = company?.emailCorporativo || company?.email || "";
    const companyCnpj = company?.cnpj || "";

    const matchesSearch =
      !searchTerm ||
      [companyName, companyEmail, companyCnpj]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "TODOS" || company?.statusVerificacao === statusFilter;

    const createdAt = company?.createdAt ? new Date(company.createdAt) : null;

    const fromDate = dateRange.from
      ? new Date(
          dateRange.from.getFullYear(),
          dateRange.from.getMonth(),
          dateRange.from.getDate(),
          0,
          0,
          0,
        )
      : null;
    const toDate = dateRange.to
      ? new Date(
          dateRange.to.getFullYear(),
          dateRange.to.getMonth(),
          dateRange.to.getDate(),
          23,
          59,
          59,
        )
      : null;

    const matchesCreatedFrom =
      !fromDate || (createdAt && createdAt >= fromDate);
    const matchesCreatedTo = !toDate || (createdAt && createdAt <= toDate);

    return (
      matchesSearch && matchesStatus && matchesCreatedFrom && matchesCreatedTo
    );
  });

  if (!canAccessCompanies) {
    return (
      <EmptyState
        icon={FiBriefcase}
        title="Acesso restrito"
        description="A listagem de empresas esta disponivel apenas para usuarios POWER_USER ou ADMIN."
      />
    );
  }

  return (
    <div className="fade-in page-stack">
      <div className="page-header">
        <div>
          <h2>Empresas</h2>
          <p>Lista de todas as empresas cadastradas no sistema.</p>
        </div>
      </div>

      <div className="section-card">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
            alignItems: "end",
            marginBottom: "16px",
          }}
        >
          <div className="field" style={{ margin: 0 }}>
            <label className="label">Buscar empresa</label>
            <input
              className="input"
              type="text"
              placeholder="Nome, e-mail ou CNPJ"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label">Status</label>
            <select
              className="select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="TODOS">Todos</option>
              <option value="PENDENTE">Pendente</option>
              <option value="APROVADO">Aprovado</option>
              <option value="REJEITADO">Rejeitado</option>
            </select>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label">Periodo de cadastro</label>
            <div ref={datePickerRef} style={{ position: "relative" }}>
              <button
                type="button"
                className="input"
                onClick={() => setIsDatePickerOpen((prev) => !prev)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
              >
                <span>{dateRangeLabel}</span>
                <FiCalendar />
              </button>

              {isDatePickerOpen ? (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 30,
                    marginTop: "8px",
                    background: "white",
                    border: "1px solid rgba(26, 18, 11, 0.1)",
                    borderRadius: "14px",
                    boxShadow: "0 14px 32px rgba(26, 18, 11, 0.16)",
                    padding: "10px",
                  }}
                >
                  <DayPicker
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) =>
                      setDateRange(range || { from: undefined, to: undefined })
                    }
                    numberOfMonths={1}
                  />
                  <button
                    type="button"
                    className="button button--ghost"
                    style={{ width: "100%", marginTop: "8px" }}
                    onClick={() => {
                      setDateRange({ from: undefined, to: undefined });
                      setIsDatePickerOpen(false);
                    }}
                  >
                    Limpar periodo
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="muted">Carregando empresas...</div>
        ) : companies.length === 0 ? (
          <NoData
            title="Nenhuma empresa encontrada"
            description="Não há empresas para listar no momento."
          />
        ) : filteredCompanies.length === 0 ? (
          <NoData
            title="Nenhum resultado para os filtros"
            description="Tente ajustar a busca ou o status selecionado."
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="companies-table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map((company) => (
                    <tr key={company.id}>
                      <td>
                        <span style={{ display: "block", fontWeight: 500 }}>
                          {company?.razaoSocial || company?.name || "-"}
                        </span>
                        <span style={{ display: "block", fontSize: "0.75rem", color: "var(--ink-500)" }}>
                          {company?.cnpj || "-"}
                        </span>
                      </td>
                      <td>{company?.emailCorporativo || company?.email || "-"}</td>
                      <td>
                        {company?.statusVerificacao ? (
                          <span
                            className="badge"
                            data-tone={statusTone(company.statusVerificacao)}
                          >
                            {statusLabel(company.statusVerificacao)}
                          </span>
                        ) : "-"}
                      </td>
                      <td>
                        <span>{formatDate(company?.createdAt)}</span>
                        {company?.createdAt ? (
                          <span className="muted" style={{ display: "block", fontSize: "0.78rem" }}>
                            {timeAgo(company.createdAt)}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <Link
                          className="button button--ghost"
                          to={`/empresas/${company.id}`}
                          aria-label="Visualizar empresa"
                          title="Visualizar empresa"
                          style={{ padding: "0.5rem", minWidth: "2.25rem", justifyContent: "center" }}
                        >
                          <FiEye size={16} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="companies-mobile-list">
              {filteredCompanies.map((company) => (
                <div key={company.id} className="company-mobile-card">
                  <div className="company-mobile-card__header">
                    <div>
                      <div className="company-mobile-card__name">
                        {company?.razaoSocial || company?.name || "-"}
                      </div>
                      <div className="company-mobile-card__cnpj">
                        {company?.cnpj || "-"}
                      </div>
                    </div>
                    {company?.statusVerificacao ? (
                      <span
                        className="badge"
                        data-tone={statusTone(company.statusVerificacao)}
                        style={{ flexShrink: 0 }}
                      >
                        {statusLabel(company.statusVerificacao)}
                      </span>
                    ) : null}
                  </div>
                  <div className="company-mobile-card__meta">
                    <span className="company-mobile-card__meta-item">
                      {company?.emailCorporativo || company?.email || "-"}
                    </span>
                  </div>
                  <div className="company-mobile-card__footer">
                    <div className="company-mobile-card__date">
                      <span>{formatDate(company?.createdAt)}</span>
                      {company?.createdAt ? (
                        <span style={{ display: "block" }}>{timeAgo(company.createdAt)}</span>
                      ) : null}
                    </div>
                    <Link
                      className="button button--ghost"
                      to={`/empresas/${company.id}`}
                      aria-label="Visualizar empresa"
                      style={{ padding: "0.5rem", minWidth: "2.25rem", justifyContent: "center" }}
                    >
                      <FiEye size={16} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </>

        )}
      </div>
    </div>
  );
};

export default CompaniesPage;
